import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  ListItemButton,
  Collapse,
  Divider,
  Tooltip,
  Button,
} from '@mui/material';
import { ExpandLess, ExpandMore, Schedule } from '@mui/icons-material';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import { ESPACO_ENTRE_COLUNAS, gradeDeColunas, larguraMinimaDaGrade } from '../../utils/colunasDoKanban';
import { totalDaColunaDoKanban } from '../../utils/totalDaColunaDoKanban';
import { mapPriority } from '../../redux/ticket/ticket.actions';
import {
  getTicketAssigneeDisplayName,
  getTicketAssigneeGroupKey,
  rotuloDoResponsavelComDepartamento,
} from '../../utils/ticketAssignee';
import { nomeComRepresentante, useRepresentantes } from '../../utils/nomeDoRepresentante';
import { useDepartamentos } from '../../utils/departamentoDoChamado';

const KANBAN_EXPANDED_ASSIGNEES_KEY = 'kanban-expanded-assignees';

const readExpandedAssignees = () => {
  try {
    const raw = localStorage.getItem(KANBAN_EXPANDED_ASSIGNEES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeExpandedAssignees = (state) => {
  try {
    localStorage.setItem(KANBAN_EXPANDED_ASSIGNEES_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private mode */
  }
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
};

const truncatedLineSx = {
  display: 'block',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const KANBAN_CARD_SKIP_LABELS = new Set(['Protocolo', 'Assunto', 'Responsável']);

const formatTicketSlaCompact = (value) => {
  if (value == null || value === '') return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return d.toLocaleString('pt-BR');
  }
};

const formatTicketSlaFull = (value) => {
  if (value == null || value === '') return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('pt-BR');
};

const getTicketSlaColor = (value, palette) => {
  if (value == null || value === '') return palette.text.disabled;
  const due = new Date(value).getTime();
  if (Number.isNaN(due)) return palette.text.disabled;
  const diff = due - Date.now();
  if (diff < 0) return palette.error.main;
  if (diff < 24 * 60 * 60 * 1000) return palette.warning.main;
  return palette.text.secondary;
};

const buildTicketSlaTooltip = (value) => {
  const full = formatTicketSlaFull(value);
  if (!full) return 'SLA não definido';
  const due = new Date(value).getTime();
  const overdue = !Number.isNaN(due) && due < Date.now();
  return `Previsão de solução (SLA): ${full}${overdue ? ' (vencido)' : ''}`;
};

const TruncatedText = ({ text, variant = 'caption', sx = {}, fontWeight, color }) => {
  const display = text == null || text === '' ? '' : String(text);
  if (!display) return null;
  return (
    <Tooltip title={display} arrow placement="bottom" enterDelay={400}>
      <Typography
        variant={variant}
        component="span"
        fontWeight={fontWeight}
        sx={{
          ...truncatedLineSx,
          ...(color ? { color } : {}),
          ...sx,
        }}
      >
        {display}
      </Typography>
    </Tooltip>
  );
};

const TicketKanbanView = ({ columns, ticketsByColumn, onCardClick, onStatusChange, editTicket, visibleCardFields, showGlobalColumnHeaders = false, onExpandedAssigneesChange, assigneeCounts = null, onLoadColumnMore = null, groupExtra = null, responsavelVisivel = null }) => {
  const { theme } = useContext(ThemeContext);
  // O mapa código -> nome do representante, buscado uma vez por sessão. Vazio
  // enquanto não chega, e aí o nome sai como sempre saiu.
  const representantes = useRepresentantes();
  const departamentos = useDepartamentos();
  const [draggedTicket, setDraggedTicket] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [expandedAssignees, setExpandedAssignees] = useState(readExpandedAssignees);
  const useCustomFields = Array.isArray(visibleCardFields) && visibleCardFields.length > 0;

  // Informa ao pai quais responsáveis estão expandidos (para a carga sob demanda
  // do histórico carregar apenas os grupos abertos).
  useEffect(() => {
    if (typeof onExpandedAssigneesChange === 'function') {
      onExpandedAssigneesChange(Object.keys(expandedAssignees).filter((k) => expandedAssignees[k]));
    }
  }, [expandedAssignees, onExpandedAssigneesChange]);

  const columnById = useMemo(() => {
    const map = {};
    (columns || []).forEach((c) => {
      if (c && c.id != null) {
        map[c.id] = c;
      }
    });
    return map;
  }, [columns]);

  const handleDragStart = (e, ticket) => {
    setDraggedTicket(ticket);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(ticket.id));
  };

  const handleDragOver = (e, assigneeKey, statusId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverKey(`${assigneeKey}:${statusId}`);
  };

  const handleDragLeave = () => {
    setDragOverKey(null);
  };

  const handleDrop = async (e, targetColumnId) => {
    e.preventDefault();
    setDragOverKey(null);
    if (!draggedTicket) {
      setDraggedTicket(null);
      return;
    }
    const col = columnById[targetColumnId];
    const items = Array.isArray(col?.items) ? col.items : [];
    const primary = items.length > 0 ? items[0] : null;
    if (!primary) {
      setDraggedTicket(null);
      return;
    }
    const currentStatusId = draggedTicket.status_id ?? draggedTicket.ticket_status?.id;
    const currentSubstatusId = draggedTicket.substatus_id ?? draggedTicket.ticket_substatus?.id ?? null;
    if (currentStatusId === primary.status_id && (currentSubstatusId == null ? primary.substatus_id == null : currentSubstatusId === primary.substatus_id)) {
      setDraggedTicket(null);
      return;
    }
    setDraggedTicket(null);
    if (onStatusChange && editTicket) {
      try {
        await editTicket({ ...draggedTicket, status_id: primary.status_id, substatus_id: primary.substatus_id ?? null });
      } catch (err) {
      }
    }
  };

  const priorityColor = (p) => {
    if (p === 'Baixa') return theme.palette.success.main;
    if (p === 'Média') return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const internalBadgeColor = (ticket) => {
    const isInternal = Number(ticket.is_internal) === 1;
    return isInternal ? 'orange' : 'green';
  };

  const groupTicketsByAssignee = (tickets) => {
    const groups = {};

    tickets.forEach((ticket) => {
      const assigneeId = getTicketAssigneeGroupKey(ticket);
      // O título do grupo diz QUAL departamento espera pelo chamado quando não
      // há responsável (ponto 24). Só aqui -- ver o comentário da função.
      const assigneeName = rotuloDoResponsavelComDepartamento(ticket, representantes, departamentos);

      if (!groups[assigneeId]) {
        groups[assigneeId] = {
          key: assigneeId,
          name: assigneeName,
          ticketSample: ticket,
          tickets: [],
        };
      }

      groups[assigneeId].tickets.push(ticket);
    });

    return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  };

  const toggleExpandedAssignee = (assigneeKey) => {
    setExpandedAssignees((prev) => {
      const next = { ...prev };
      next[assigneeKey] = !prev[assigneeKey];
      if (!next[assigneeKey]) delete next[assigneeKey];
      writeExpandedAssignees(next);
      return next;
    });
  };

  const buildTicketsByColumnForAssignee = (assigneeKey) => {
    const byCol = {};
    (columns || []).forEach((col) => {
      byCol[col.id] = [];
    });
    (Object.entries(ticketsByColumn || {}) || []).forEach(([colId, list]) => {
      const safeList = Array.isArray(list) ? list : [];
      const filtered = safeList.filter((t) => getTicketAssigneeGroupKey(t) === assigneeKey);
      byCol[colId] = filtered;
    });
    return byCol;
  };

  const extraCardFields = useMemo(() => {
    if (!useCustomFields) return [];
    return visibleCardFields.filter(({ label }) => !KANBAN_CARD_SKIP_LABELS.has(label));
  }, [useCustomFields, visibleCardFields]);

  const renderTicketCard = (ticket) => {
    const assigneeName = getTicketAssigneeDisplayName(ticket, representantes);
    const slaCompact = formatTicketSlaCompact(ticket.solution_due_at);
    const slaDisplay = slaCompact ?? '—';
    const slaColor = getTicketSlaColor(ticket.solution_due_at, theme.palette);
    const slaTooltip = buildTicketSlaTooltip(ticket.solution_due_at);
    return (
      <Card
        key={ticket.id}
        draggable
        onDragStart={(e) => handleDragStart(e, ticket)}
        onClick={() => onCardClick && onCardClick(ticket)}
        elevation={0}
        sx={{
          cursor: 'grab',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          position: 'relative',
          pl: 1.25,
          opacity: draggedTicket?.id === ticket.id ? 0.6 : 1,
          '&:active': { cursor: 'grabbing' },
          transition: 'box-shadow 0.15s ease',
          '&:hover': { boxShadow: theme.shadows[1] },
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 3,
            bottom: 3,
            width: 3,
            borderTopLeftRadius: 4,
            borderBottomLeftRadius: 4,
            backgroundColor: internalBadgeColor(ticket),
          },
        }}
      >
        <CardContent sx={{ py: 0.5, px: 0.75, '&:last-child': { pb: 0.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: `${theme.palette.text.secondary} !important`,
                fontWeight: 600,
                fontSize: '0.68rem',
                lineHeight: 1.15,
              }}
            >
              {/* protocolo por tenant, nao a PK: o arrastar/soltar e a
                  identidade do cartao continuam usando ticket.id */}
              {ticket.number}
            </Typography>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                flexShrink: 0,
                backgroundColor: priorityColor(mapPriority(ticket.priority)),
                opacity: 0.85,
              }}
            />
          </Box>

          <TruncatedText
            text={ticket.title || '-'}
            fontWeight={600}
            color={`${theme.palette.text.primary} !important`}
            sx={{ fontSize: '0.72rem', lineHeight: 1.2, mt: 0.125 }}
          />

          {extraCardFields.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, minWidth: 0, mt: 0.125 }}>
              {extraCardFields.map(({ label, getValue }) => {
                const value = getValue(ticket);
                if (value == null || value === '') return null;
                return (
                  <TruncatedText
                    key={label}
                    text={value}
                    color={`${theme.palette.text.secondary} !important`}
                    sx={{ fontSize: '0.68rem', lineHeight: 1.2 }}
                  />
                );
              })}
            </Box>
          )}

          {!useCustomFields && ticket.ticket_substatus && (
            /*
             * A JUSTIFICATIVA em até DUAS linhas (Gisele, 19/10/2026):
             * "aumentar o espaço para visualizar melhor".
             *
             * Ela cabia numa linha só, cortada no meio: "Aguardando retorno
             * do cliente" e "Aguardando retorno do fornecedor" apareciam
             * iguais no cartão, e só o tooltip (que pede parar o mouse em
             * cima) desfazia a confusão.
             *
             * DUAS e não ilimitado: o cartão é a unidade de comparação do
             * kanban, e uma justificativa longa empurraria os outros cartões
             * coluna abaixo. O tooltip continua, para o caso de passar disso.
             */
            <Tooltip title={ticket.ticket_substatus.name} arrow placement="bottom" enterDelay={400}>
              <Chip
                size="small"
                label={ticket.ticket_substatus.name}
                sx={{
                  mt: 0.125,
                  height: 'auto',
                  minHeight: 16,
                  fontSize: '0.65rem',
                  maxWidth: '100%',
                  '& .MuiChip-label': {
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    whiteSpace: 'normal',
                    lineHeight: 1.2,
                    px: 0.5,
                    py: 0.125,
                  },
                }}
              />
            </Tooltip>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 0.5,
              mt: 0.25,
              minWidth: 0,
            }}
          >
            <Tooltip title={slaTooltip} arrow placement="bottom" enterDelay={400}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                  minWidth: 0,
                  flex: 1,
                  maxWidth: '48%',
                }}
              >
                <Schedule sx={{ fontSize: 12, color: slaColor, flexShrink: 0 }} />
                <Typography
                  variant="caption"
                  component="span"
                  sx={{
                    ...truncatedLineSx,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    lineHeight: 1.15,
                    fontVariantNumeric: 'tabular-nums',
                    color: `${slaColor} !important`,
                  }}
                >
                  {slaDisplay}
                </Typography>
              </Box>
            </Tooltip>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0.375,
                minWidth: 0,
                flex: 1,
                maxWidth: '52%',
              }}
            >
              <TruncatedText
                text={assigneeName}
                color={`${theme.palette.text.secondary} !important`}
                sx={{ fontSize: '0.65rem', lineHeight: 1.15 }}
              />
              <Avatar
                sx={{
                  width: 16,
                  height: 16,
                  fontSize: '0.58rem',
                  bgcolor: theme.palette.primary.main,
                  flexShrink: 0,
                }}
              >
                {getInitials(assigneeName)}
              </Avatar>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderKanbanGrid = (assigneeKey, ticketsByColumnScope, { hideColumnHeaders = false } = {}) => {
    // Agregado deste responsável (com by_status) para saber o total real de cada
    // coluna e mostrar "carregado/total" + o botão "Carregar mais".
    const agg = Array.isArray(assigneeCounts)
      ? assigneeCounts.find((g) => String(g.key) === String(assigneeKey))
      : null;
    /*
     * A conta mora em `utils/totalDaColunaDoKanban`, e nao aqui.
     *
     * Esta era a TERCEIRA copia da mesma soma -- havia uma na `TicketPage`
     * (que decide se vale pedir a pagina da coluna) e esta (que desenha o
     * numero). Elas ja divergiam: so esta deduplicava os status repetidos.
     *
     * E foi a divergencia que escondeu o defeito: consertar a da `TicketPage`
     * em 23/10/2026 nao mudou NADA na tela, porque o numero vinha daqui.
     */
    const colTotalFor = (col) => totalDaColunaDoKanban(agg, col);
    return (
      <Box
        sx={{
          display: 'grid',
          // A medida mora em `utils/colunasDoKanban`, e nao aqui, porque o
          // kanban e desenhado por DOIS componentes: a faixa de cabecalhos e
          // da `TicketPage`. Enquanto a medida esteve nos dois lugares eles
          // divergiram -- o cabecalho espremia a 167px enquanto o cartao era
          // desenhado com 260px, e o rotulo deixava de ficar em cima da coluna
          // que nomeava.
          gridTemplateColumns: gradeDeColunas(columns && columns.length),
          gap: `${ESPACO_ENTRE_COLUNAS}px`,
          minHeight: 280,
          // `minWidth`, e nao `overflowX: auto` aqui.
          //
          // O scroll horizontal passou a ser UM so, no wrapper da tela, para
          // o cabecalho e TODOS os grupos de responsavel andarem juntos. Com
          // um `overflowX` por grid, cada grupo rolava por conta propria:
          // rolar o grupo de um responsavel nao movia nem o cabecalho nem os
          // outros grupos.
          minWidth: `${larguraMinimaDaGrade(columns && columns.length)}px`,
        }}
      >
        {columns.map((col) => {
          const tickets = (ticketsByColumnScope?.[col.id] || []);
          const total = agg ? colTotalFor(col) : tickets.length;
          const remaining = Math.max(0, total - tickets.length);
          // Só há "mais" quando o servidor diz que há próxima página (hasMore da
          // paginação por coluna). O agregado por status pode superestimar (ex.:
          // substatus/condição do filtro deixam menos na coluna), então NÃO usamos
          // "total - carregado" para decidir o botão — senão ele carregaria nada.
          const cell = groupExtra?.[assigneeKey]?.[col.id];
          const hasMore = !!cell?.hasMore;
          const isOver = dragOverKey === `${assigneeKey}:${col.id}`;
          return (
            <Box
              key={col.id}
              onDragOver={(e) => handleDragOver(e, assigneeKey, col.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, col.id)}
              sx={{
                borderRadius: 2,
                bgcolor: isOver ? theme.palette.action.hover : theme.palette.background.paper,
                border: `1px solid ${isOver ? theme.palette.primary.main : theme.palette.divider}`,
                p: 0.75,
                transition: 'border-color 0.2s, background-color 0.2s',
              }}
            >
              {!hideColumnHeaders && (
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ color: theme.palette.text.primary, minWidth: 0 }}>
                    {col.name}
                  </Typography>
                  <Typography variant="caption" sx={{ flexShrink: 0, color: `${theme.palette.text.secondary} !important` }}>
                    {agg ? `${tickets.length}/${total}` : tickets.length}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {tickets.map((ticket) => renderTicketCard(ticket))}
                {hasMore && typeof onLoadColumnMore === 'function' && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => onLoadColumnMore(assigneeKey, col)}
                    sx={{ mt: 0.25, textTransform: 'none', fontSize: '0.72rem', color: theme.palette.primary.main, alignSelf: 'stretch' }}
                  >
                    Carregar mais{remaining > 0 ? ` (${remaining})` : ''}
                  </Button>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderGlobalColumnHeaders = () => null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, overflow: 'visible', pb: 1, minHeight: 360 }}>
      {(() => {
        const allTickets = Object.values(ticketsByColumn || {}).flat();
        const loadedGroups = groupTicketsByAssignee(allTickets);
        // Prefere os grupos vindos do agregado (contagem real de TODOS os
        // responsáveis, mesmo os sem ticket carregado ainda). Cada grupo carrega
        // sua 1ª página só ao expandir. Cai para os grupos dos tickets carregados
        // se o agregado ainda não chegou.
        let groups;
        if (Array.isArray(assigneeCounts) && assigneeCounts.length > 0) {
            const loadedByKey = {};
            loadedGroups.forEach((g) => { loadedByKey[g.key] = g; });
            const seen = new Set();
            groups = assigneeCounts.map((ac) => {
                seen.add(String(ac.key));
                // O MESMO rótulo do outro caminho de agrupamento. Este
                // agregado traz o nome pronto do servidor, então sem isto a
                // mesma tela mostraria o representante ou não conforme o
                // agregado estivesse carregado.
                return {
                    key: ac.key,
                    name: nomeComRepresentante(ac.name, representantes[String(ac.representante_codigo)]),
                    count: ac.count,
                    tickets: loadedByKey[ac.key]?.tickets || [],
                };
            });
            // Segurança: mantém grupos com tickets carregados que não vieram no agregado.
            loadedGroups.forEach((g) => {
                if (!seen.has(String(g.key))) groups.push({ ...g, count: g.tickets.length });
            });
        } else {
            groups = loadedGroups.map((g) => ({ ...g, count: g.tickets.length }));
        }

        /*
         * O recorte por departamento vale para os DOIS caminhos acima.
         *
         * O de cima (agregado) já vinha recortado da `TicketPage`. O de baixo,
         * não -- e é justamente ele que roda durante a BUSCA, porque buscar
         * desliga o agregado. Resultado relatado pela Gisele: "busca por
         * dentro do kanban aparece nome de outros operadores que não pertencem
         * a este kanban".
         *
         * Aplicar aqui, depois de montar, cobre os dois de uma vez -- inclusive
         * o `push` de segurança logo acima, que reinjeta grupos carregados que
         * não vieram no agregado e vazava pelo mesmo buraco.
         */
        if (responsavelVisivel) {
            groups = groups.filter((g) => responsavelVisivel(g.key));
        }

        if (groups.length === 0) return null;

        /*
         * `overflowX: visible`, e nao `hidden`.
         *
         * Com `hidden` este Box CORTAVA a parte das colunas que passa da
         * largura da tela, e o scroll horizontal do wrapper nunca alcancava o
         * que estava escondido aqui dentro. O recorte agora e do wrapper, que
         * e quem rola.
         */
        return (
          <Box sx={{ overflowX: 'visible', minHeight: 0 }}>
            {showGlobalColumnHeaders && columns?.length > 0 && renderGlobalColumnHeaders()}
            {groups.map((group, idx) => {
              const isExpanded = !!expandedAssignees[group.key];
              const scoped = isExpanded ? buildTicketsByColumnForAssignee(group.key) : null;
              return (
                <Box key={group.key} sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => toggleExpandedAssignee(group.key)}
                    selected={isExpanded}
                    sx={{
                      py: 0.5,
                      px: 0.75,
                      borderRadius: 1.25,
                      '&:hover': { backgroundColor: theme.palette.action.hover },
                      '&.Mui-selected': { backgroundColor: theme.palette.action.selected },
                      '&.Mui-selected:hover': { backgroundColor: theme.palette.action.selected },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%', minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0, flex: 1 }}>
                        <Typography
                          noWrap
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            color: theme.palette.text.primary,
                            minWidth: 0,
                          }}
                        >
                          {group.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                          <Typography
                            noWrap
                            sx={{
                              fontSize: '0.75rem',
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {group.count} ticket(s)
                          </Typography>
                          {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </Box>
                      </Box>
                    </Box>
                  </ListItemButton>

                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ px: 0.75, pt: 0.5, pb: 1 }}>
                      {renderKanbanGrid(group.key, scoped, { hideColumnHeaders: showGlobalColumnHeaders })}
                    </Box>
                  </Collapse>
                  {idx < groups.length - 1 && <Divider sx={{ mt: 0.5, mb: 0.5, opacity: 0.6 }} />}
                </Box>
              );
            })}
          </Box>
        );
      })()}
    </Box>
  );
};

export default TicketKanbanView;
